import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PromptAnalysis } from '@brand24-original/llm-types';
import { getPromptAnalyses } from '../../services/prompt-analysis/PromptAnalysisService';
import { GET_PROMPT_ANALYSES, GET_PROMPT_ANALYSIS_USAGE } from '../../query/prompt-analysis/keys';

const POLL_MS = 5_000;

const isPending = (a: PromptAnalysis) => a.status === 'PENDING' || a.status === 'RUNNING';

/** The project's review history; polls while any review is still being computed by the worker. */
export const useGetPromptAnalyses = (projectId: number | null) => {
  const queryClient = useQueryClient();
  const query = useQuery<PromptAnalysis[]>({
    queryKey: [GET_PROMPT_ANALYSES, projectId],
    queryFn: () => getPromptAnalyses(projectId!),
    enabled: projectId != null,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => (q.state.data?.some(isPending) ? POLL_MS : false),
  });

  // A review that finally FAILED gives its quota slot back — refresh the usage once nothing is pending.
  const pendingCount = query.data?.filter(isPending).length ?? 0;
  const hadPendingRef = useRef(false);
  useEffect(() => {
    if (hadPendingRef.current && pendingCount === 0) {
      void queryClient.invalidateQueries({ queryKey: [GET_PROMPT_ANALYSIS_USAGE] });
    }
    hadPendingRef.current = pendingCount > 0;
  }, [pendingCount, queryClient]);

  return query;
};
